/**
 * @module lib/rpdm/refusalCodes
 * @summary Server refusal-code registry with unknown-code fail-safe.
 * @description Maps every catalogued W2-A → W2-G server refusal code to a
 * translated `{ code, title, body, level, retriable }` entry. Every entry
 * emits with a deterministic `data-testid="refusal-toast-{code}"` when
 * rendered by `RefusalToast` (Phase 1b).
 *
 * ## Purpose
 * - Give the UI a single source of truth for every server refusal.
 * - Prevent silent swallowing of server responses when a new code
 *   appears — the unknown-code fail-safe (§ 4.2, § 11.0-E) surfaces a
 *   generic error and records a warning so the omission is auditable.
 *
 * ## Inputs / Outputs
 * - `translate(code)` — takes a server refusal code string;
 *   returns a registered entry or the `refusal_unknown` sentinel.
 *
 * ## Invariants (Phase 1a)
 * - Side-effect free at import time. Registry is a module-scope frozen object.
 * - `translate` NEVER returns `undefined` and NEVER throws.
 * - Unknown-code fail-safe emits a `console.warn` in development builds only
 *   (auto-stripped in production per CRA behaviour). No `console` call runs
 *   at import time.
 * - Registry entries are immutable (`Object.freeze` on each row + on the
 *   registry map) so downstream code cannot mutate the shared source.
 *
 * ## Future consuming phase
 * - Phase 1b: `RefusalToast.jsx` renders returned entries via `sonner`.
 * - Phase 2 → 5: every W2 wrapper feeds `error.code` through `translate`.
 * - § 4.6 regression asserts an unknown code returns the sentinel.
 */

/**
 * @typedef {'error' | 'warn' | 'info'} RefusalLevel
 */

/**
 * @typedef {object} RefusalEntry
 * @property {string} code - the machine-readable code returned by the server.
 * @property {string} title - short human title for toast headers.
 * @property {string} body - longer body copy for tooltips / drawers.
 * @property {RefusalLevel} level - severity for `sonner` colour + a11y live-region tone.
 * @property {boolean} retriable - whether the caller may re-issue the same request.
 */

const REGISTRY = Object.freeze({
    // ─── W2-B · hierarchy ─────────────────────────────────────────────
    cycle_detected: Object.freeze({
        code: "cycle_detected",
        title: "Cycle detected",
        body: "This change would create a cycle in the work-item hierarchy. Choose a different parent.",
        level: "error",
        retriable: false,
    }),
    max_depth_exceeded: Object.freeze({
        code: "max_depth_exceeded",
        title: "Hierarchy too deep",
        body: "Work items may not be nested more than five levels deep.",
        level: "error",
        retriable: false,
    }),
    cross_engagement_link_forbidden: Object.freeze({
        code: "cross_engagement_link_forbidden",
        title: "Cross-engagement link forbidden",
        body: "Work items can only reference parents inside the same engagement.",
        level: "error",
        retriable: false,
    }),
    // ─── W2-C · reviewers ─────────────────────────────────────────────
    reviewer_policy_conflict: Object.freeze({
        code: "reviewer_policy_conflict",
        title: "Reviewer policy conflict",
        body: "The chosen reviewer conflicts with an active workflow policy.",
        level: "error",
        retriable: false,
    }),
    reviewer_capability_missing: Object.freeze({
        code: "reviewer_capability_missing",
        title: "Reviewer capability missing",
        body: "The selected reviewer lacks a required capability for this work-item type.",
        level: "error",
        retriable: false,
    }),
    // ─── W2-D · lifecycle ─────────────────────────────────────────────
    illegal_transition: Object.freeze({
        code: "illegal_transition",
        title: "Illegal transition",
        body: "This state transition is not permitted under the active workflow configuration.",
        level: "error",
        retriable: false,
    }),
    // ─── W2-E · dependencies ──────────────────────────────────────────
    dependency_cycle_detected: Object.freeze({
        code: "dependency_cycle_detected",
        title: "Dependency cycle",
        body: "The proposed dependency would create a cycle. Choose different endpoints.",
        level: "error",
        retriable: false,
    }),
    dependency_type_invalid: Object.freeze({
        code: "dependency_type_invalid",
        title: "Invalid dependency type",
        body: "The provided dependency type is not one of FS, SS, FF, SF.",
        level: "error",
        retriable: false,
    }),
    // ─── W2-F · progress ──────────────────────────────────────────────
    snapshot_stale: Object.freeze({
        code: "snapshot_stale",
        title: "Snapshot stale",
        body: "The progress snapshot has changed since you loaded it. Refresh and try again.",
        level: "warn",
        retriable: true,
    }),
    override_conflict: Object.freeze({
        code: "override_conflict",
        title: "Override conflict",
        body: "Another user recorded a progress override for the same target. Refresh and try again.",
        level: "warn",
        retriable: true,
    }),
    recalc_in_progress: Object.freeze({
        code: "recalc_in_progress",
        title: "Recalculation in progress",
        body: "A progress recalculation is already running for this engagement.",
        level: "info",
        retriable: true,
    }),
    // ─── W2-G · effort ────────────────────────────────────────────────
    idempotency_key_required: Object.freeze({
        code: "idempotency_key_required",
        title: "Idempotency-Key required",
        body: "This write requires an Idempotency-Key header. Reload and try again.",
        level: "error",
        retriable: true,
    }),
    idempotency_key_replay_conflict: Object.freeze({
        code: "idempotency_key_replay_conflict",
        title: "Duplicate submission",
        body: "This request has already been submitted with a different payload.",
        level: "warn",
        retriable: false,
    }),
    effort_planned_version_stale: Object.freeze({
        code: "effort_planned_version_stale",
        title: "Planned effort out of date",
        body: "Someone updated planned effort while you were editing. Refresh to see the latest values.",
        level: "warn",
        retriable: true,
    }),
    effort_entry_self_approval_forbidden: Object.freeze({
        code: "effort_entry_self_approval_forbidden",
        title: "Self-approval forbidden",
        body: "You cannot approve an effort entry you created.",
        level: "error",
        retriable: false,
    }),
    effort_bypass_on_own_entry_forbidden: Object.freeze({
        code: "effort_bypass_on_own_entry_forbidden",
        title: "Bypass forbidden",
        body: "Super-admin bypass is not permitted on your own effort entries.",
        level: "error",
        retriable: false,
    }),
    effort_reversal_target_missing: Object.freeze({
        code: "effort_reversal_target_missing",
        title: "Reversal target missing",
        body: "The entry you tried to reverse could not be found.",
        level: "error",
        retriable: false,
    }),
    effort_correction_target_immutable: Object.freeze({
        code: "effort_correction_target_immutable",
        title: "Correction target immutable",
        body: "The entry you tried to correct has been finalised and cannot be edited in place.",
        level: "error",
        retriable: false,
    }),
    // ─── Cross-cutting ────────────────────────────────────────────────
    feature_disabled_404: Object.freeze({
        code: "feature_disabled_404",
        title: "Feature not enabled",
        body: "This feature is disabled on the current environment.",
        level: "info",
        retriable: false,
    }),
    permission_denied_403: Object.freeze({
        code: "permission_denied_403",
        title: "Permission denied",
        body: "You do not have permission to perform this action.",
        level: "error",
        retriable: false,
    }),
    stale_pending: Object.freeze({
        code: "stale_pending",
        title: "Stale · pending recalculation",
        body: "Displayed values may lag until the next recalculation completes.",
        level: "info",
        retriable: false,
    }),
    capability_missing: Object.freeze({
        code: "capability_missing",
        title: "Feature not available on this environment",
        body: "The required backend endpoint has not been enabled here. The surface is fail-closed until it ships.",
        level: "info",
        retriable: false,
    }),
    // ─── Rev.1a · unknown-code fail-safe (§ 11.0-E) ──────────────────
    refusal_unknown: Object.freeze({
        code: "refusal_unknown",
        title: "Unexpected server response",
        body: "The server returned a code the UI does not recognise. Contact administrator; include the trace id if available.",
        level: "error",
        retriable: false,
    }),
});

/**
 * @param {string | null | undefined} code - server refusal code.
 * @returns {RefusalEntry} the registered entry, or the `refusal_unknown` sentinel.
 * @invariant Never returns `undefined`. Never throws.
 * @invariant Logs `console.warn('rpdm:refusal_unknown', code)` in
 *            `process.env.NODE_ENV !== 'production'` builds only. The warn
 *            call is guarded so the module remains side-effect free at
 *            import time.
 */
export function translate(code) {
    if (typeof code === "string" && Object.prototype.hasOwnProperty.call(REGISTRY, code)) {
        return REGISTRY[code];
    }
    if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
        console.warn("rpdm:refusal_unknown", code);
    }
    return REGISTRY.refusal_unknown;
}

/**
 * @returns {ReadonlyArray<string>} every registered code (sorted, frozen).
 * @invariant Side-effect free; new array on each call so callers cannot
 *            mutate the frozen internal registry.
 */
export function listCodes() {
    return Object.freeze(Object.keys(REGISTRY).sort());
}
